import { Field, Label } from '@headlessui/react';
import React, { FC } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { EditIcon, EyeIconSmall, ThreeDotsIcon, TrashBinIcon } from '../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button, { ButtonDisableStyle } from '../../../components/button/index';
import { Checkbox } from '../../../components/checkbox/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/popover/index';
import { Cursor, Tooltip } from '../../../components/tooltip/index';
import { Company, SafeHooks, useFormat, useLocale, useModalState } from '../../../hooks/index';
import {
  DetailsTab,
  getConversionMethodTitle,
  safeCapTypeTitle,
  SafeWithPermissions,
} from '../../../types/safes.types';
import { getTranslation } from '../../../utils/get-translation.util';
import { toMathRound } from '../../../utils/to-math-round.util';
import { toNumber } from '../../../utils/to-number.util';
import { DeleteSafeModal } from '../modals/delete/index';

export type SafeTableItemProps = {
  handleConvert: (id: string) => void;
  handleDelete: (id: string) => void;
  handleEdit: (id: string) => void;
  handleEditConversion: (id: string) => void;
  handleView: (id: string) => void;
  handleViewConversion: (id: string) => void;
  isSelected: (id: string) => boolean;
  onRemoveSelect: (id: string) => void;
  onSelect: (id: string) => void;
  safe: SafeWithPermissions;
  selectedTab: DetailsTab;
};

export const SafeTableItem: FC<SafeTableItemProps> = ({
  isSelected,
  onSelect,
  onRemoveSelect,
  handleView,
  handleEdit,
  handleConvert,
  handleDelete,
  handleViewConversion,
  safe,
  selectedTab,
  handleEditConversion,
}) => {
  const {
    id,
    stakeholder,
    investment,
    conversionDate,
    conversionMethod,
    convertedShares,
    floorValue,
    issueDate,
    maturityDate,
    discount,
    capValue,
    capType,
    conversion,
    canDelete,
    canEdit,
  } = safe;

  const { messagesLocale } = useLocale();
  const { deleteConversion } = SafeHooks.useDeleteConversion();

  const handleDeleteSafeConversion = () => {
    deleteConversion(conversion?.id || '', {
      onSuccess: () => {
        onRemoveSelect(id);
        toast.success(getTranslation('SAFE_DELETED_SUCCESS'));
      },
    });
  };

  const { format } = useFormat();
  const { selectedCompany } = Company.useSelected();

  const { handleCloseModal, handleSuccessModal, handleOpenModal, isOpen, toggler } = useModalState({
    onSuccess: () => {
      if (conversion) {
        handleDeleteSafeConversion();
      } else handleDelete(id);
    },
  });

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: toggler, open: isOpen }}>
        <DeleteSafeModal
          onClose={handleCloseModal}
          onSuccess={handleSuccessModal}
          type={conversion ? 'conversion' : 'default'}
        />
      </AlertDialogWrapper>
      <tr className={twMerge('w-full bg-white', isSelected(id) && 'bg-gray-50')}>
        {selectedTab === DetailsTab.PENDING_CONVERSION && (
          <td className="relative">
            <div className="flex h-full w-full cursor-pointer items-center justify-center">
              <Field className="flex items-center">
                <Checkbox
                  checked={isSelected(id)}
                  id={id}
                  onChange={(checked) => {
                    checked ? onSelect(id) : onRemoveSelect(id);
                  }}
                />
                <Label
                  className="absolute left-0 top-0 h-full w-full cursor-pointer"
                  htmlFor={id}
                />
              </Field>
            </div>
          </td>
        )}
        <td className="flex min-w-[120px] max-w-[308px] items-center gap-3 p-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-[450] text-gray-700">
            {stakeholder.fullName[0]}
          </div>
          <Tooltip childrenWrapperClassName="max-w-[250px]" content={stakeholder.fullName}>
            <span className="truncate text-nowrap text-sm font-[450] text-gray-700">
              {stakeholder.fullName}
            </span>
          </Tooltip>
        </td>
        <td className="max-w-[151px] truncate p-4 text-sm text-gray-700">
          {toNumber(investment)?.toLocaleString('en-US')}
        </td>
        {selectedTab === DetailsTab.PENDING_CONVERSION && (
          <td className="max-w-[113px] truncate p-4 text-sm text-gray-700">
            {issueDate ? format(issueDate, 'dd/MM/yyyy') : '-'}
          </td>
        )}
        {selectedTab === DetailsTab.PENDING_CONVERSION ? (
          <>
            <td className="max-w-[149px] truncate p-4 text-sm text-gray-700">
              {capValue ? toNumber(capValue)?.toLocaleString('en-US') : '-'}
            </td>
            <td className="max-w-[120px] truncate p-4 text-sm text-gray-700">
              {capValue && capType ? <AppFormattedMessage id={safeCapTypeTitle[capType]} /> : ''}
            </td>
            <td className="max-w-[164px] truncate p-4 text-sm text-gray-700">
              {discount ? `${discount * 100}%` : '-'}
            </td>
            <td className="max-w-[100px] truncate p-4 text-sm text-gray-700">
              {floorValue ? toNumber(floorValue)?.toLocaleString('en-US') : '-'}
            </td>
            <td className="max-w-[113px] truncate p-4 text-sm text-gray-700">
              {maturityDate ? format(maturityDate, 'dd/MM/yyyy') : '-'}
            </td>
          </>
        ) : (
          <>
            <td className="max-w-[113px] truncate p-4 text-sm text-gray-700">
              {conversionDate ? format(conversionDate, 'dd/MM/yyyy') : '-'}
            </td>

            <td className="p-4 text-sm text-gray-700">
              {conversionMethod ? (
                <AppFormattedMessage id={getConversionMethodTitle(conversionMethod, capType)} />
              ) : (
                '-'
              )}
            </td>

            {/*  */}
            <td className="max-w-[100px] truncate p-4 text-sm text-gray-700">
              {conversion?.valuation?.name || '-'}
            </td>
            <td className="max-w-[100px] truncate p-4 text-sm text-gray-700">
              {selectedCompany?.currency?.symbol}{' '}
              {toMathRound(conversion?.postConversionSharePrice)?.toLocaleString('en-US') || '-'}
            </td>
            <td className="max-w-[100px] truncate p-4 text-sm text-gray-700">
              {toNumber(convertedShares)?.toLocaleString('en-US')}
            </td>
          </>
        )}
        <td className="px-4 py-[22px]">
          <Popover>
            <PopoverTrigger className="h-fit w-fit rounded data-[state=open]:shadow-md">
              <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
                <ThreeDotsIcon />
              </div>
            </PopoverTrigger>
            <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
              <PopoverClose>
                <div
                  className="flex w-full items-center justify-start gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() =>
                    selectedTab === DetailsTab.PENDING_CONVERSION
                      ? handleView(id)
                      : handleViewConversion(conversion?.id || '')
                  }
                >
                  <EyeIconSmall />
                  <span className="text-sm font-normal text-gray-700">
                    <AppFormattedMessage id="VIEW_SAFE" />
                  </span>
                </div>

                {selectedTab === DetailsTab.PENDING_CONVERSION && (
                  <Tooltip
                    className="w-[550px]"
                    content={messagesLocale['SAFE_CONVERSION_MODIFY_ACTION_BLOCKED']}
                    cursor={canEdit ? Cursor.POINTER : Cursor.DEFAULT}
                    disabled={canEdit}
                  >
                    <Button
                      className="flex w-full items-center justify-start gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                      data-permission-required
                      disabled={!canEdit}
                      disabledStyle={ButtonDisableStyle.NONE}
                      onClick={() =>
                        selectedTab === DetailsTab.PENDING_CONVERSION
                          ? handleEdit(id)
                          : handleEditConversion(conversion?.id || '')
                      }
                      styleType="NONE"
                    >
                      <EditIcon iconColor={canEdit ? undefined : '#98A2B3'} />
                      <span
                        className={twMerge(
                          'text-sm font-normal text-gray-700',
                          !canEdit && 'text-gray-400',
                        )}
                      >
                        <AppFormattedMessage id="EDIT_SAFE" />
                      </span>
                    </Button>
                  </Tooltip>
                )}

                {selectedTab === DetailsTab.PENDING_CONVERSION && (
                  <div
                    className="flex w-full items-center justify-start gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                    data-permission-required
                    onClick={() => handleConvert(id)}
                  >
                    <EditIcon />
                    <span className="text-sm font-normal text-gray-700">
                      <AppFormattedMessage id="CONVERT_SAFE" />
                    </span>
                  </div>
                )}

                <Tooltip
                  className="w-[550px]"
                  content={messagesLocale['SAFE_CONVERSION_MODIFY_ACTION_BLOCKED']}
                  cursor={canDelete ? Cursor.POINTER : Cursor.DEFAULT}
                  disabled={canDelete}
                >
                  <Button
                    className="flex w-full items-center justify-start gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                    data-permission-required
                    disabled={!canDelete}
                    disabledStyle={ButtonDisableStyle.NONE}
                    onClick={handleOpenModal}
                    styleType="NONE"
                  >
                    <TrashBinIcon
                      className="size-4"
                      iconColor={canDelete ? '#F04438' : '#98A2B3'}
                    />
                    <span
                      className={twMerge(
                        'text-sm font-normal text-fireside-500',
                        !canDelete && 'text-gray-400',
                      )}
                    >
                      <AppFormattedMessage id="DELETE_SAFE" />
                    </span>
                  </Button>
                </Tooltip>
              </PopoverClose>
            </PopoverContent>
          </Popover>
        </td>
      </tr>
    </>
  );
};
